@let isLoading = $isLoading();
@let errorText = $errorText();
@let visibleRows = $visibleRows();
@let stats = $stats();
@let workflowOptions = $workflowOptions();

<div
  class="flex max-w-[1100px] w-full min-w-0 max-h-[96dvh] flex-col overflow-hidden p-1 text-(--tui-text-primary) sm:max-h-[78vh] sm:p-4"
>
  <header class="border-b border-(--tui-background-neutral-1-hover) pb-3">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <p
          class="x-font-body-5-bold uppercase tracking-[0.08em] text-(--tui-text-secondary)"
        >
          {{ 'workflows.events_dialog.header' | translate }}
        </p>
        <h2
          class="x-font-body-1-bold mt-1 break-words text-(--tui-text-primary)"
        >
          {{ $headerTitle() | translate }}
        </h2>
      </div>

      @if (!isLoading && !errorText) {
        <div
          class="mr-5 shrink-0 rounded-full bg-(--tui-background-neutral-1-hover) px-3 py-1 x-font-body-5-bold text-(--tui-text-primary)"
        >
          {{ visibleRows.length }}
        </div>
      }
    </div>
  </header>

  @if (isLoading) {
    <div class="flex min-h-[220px] flex-col items-stretch justify-center gap-3">
      <tui-loader [inheritColor]="true" size="l"></tui-loader>
      <p class="x-font-body-4 text-(--tui-text-secondary)">
        {{ 'workflows.events_dialog.loading' | translate }}
      </p>
    </div>
  } @else if (errorText) {
    <div
      class="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-2xl border border-(--tui-background-neutral-1-hover) bg-(--tui-background-base-alt) px-4 py-6 text-center"
    >
      <tui-icon
        icon="@tui.circle-alert"
        class="h-10! w-10! text-[2.5rem] x-text-red"
      />
      <div class="space-y-1">
        <p class="x-font-body-3-bold text-(--tui-text-primary)">
          {{ 'workflows.events_dialog.error_title' | translate }}
        </p>
        <p class="x-font-body-4 text-(--tui-text-secondary)">
          {{ errorText }}
        </p>
      </div>
    </div>
  } @else if (!$state().events.length) {
    <div
      class="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-2xl border border-(--tui-background-neutral-1-hover) bg-(--tui-background-base-alt) px-4 py-6 text-center"
    >
      <tui-icon
        icon="@tui.info"
        class="h-10! w-10! text-[2.5rem] x-text-dark-75"
      />
      <div class="space-y-1">
        <p class="x-font-body-3-bold text-(--tui-text-primary)">
          {{ 'workflows.events_dialog.empty_title' | translate }}
        </p>
        <p class="x-font-body-4 text-(--tui-text-secondary)">
          {{ 'workflows.events_dialog.empty_description' | translate }}
        </p>
      </div>
    </div>
  } @else {
    <section
      class="flex flex-col gap-2 border-b border-(--tui-background-neutral-1-hover) py-2"
    >
      <tui-textfield tuiTextfieldSize="s">
        <input
          tuiInput
          [formControl]="queryControl"
          [placeholder]="
            'workflows.events_dialog.search_placeholder' | translate
          "
        />
      </tui-textfield>

      <div class="flex flex-wrap gap-1">
        @for (option of filterOptions; track option.label) {
          <button
            tuiButton
            type="button"
            size="xs"
            [appearance]="$filter() === option.value ? 'primary' : 'secondary'"
            (click)="setFilter(option.value)"
          >
            {{ option.label }}
          </button>
        }
      </div>

      <label class="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          [checked]="$hideQuiet()"
          #quietToggle
          (change)="setHideQuiet(quietToggle.checked)"
        />
        <span class="x-font-body-5 text-(--tui-text-secondary)">{{
          'workflows.events_dialog.hide_quiet_sync' | translate
        }}</span>
      </label>

      @if (workflowOptions.length > 1) {
        <div class="flex flex-wrap items-center gap-1">
          <span class="x-font-body-5 text-(--tui-text-secondary)">{{
            'workflows.events_dialog.regulations' | translate
          }}</span>
          <button
            tuiButton
            type="button"
            size="xs"
            [appearance]="
              $selectedWorkflow() === null ? 'primary' : 'secondary'
            "
            (click)="setWorkflow(null)"
          >
            {{ 'workflows.events_dialog.all' | translate }}
          </button>
          @for (workflow of workflowOptions; track workflow.id) {
            <button
              tuiButton
              type="button"
              size="xs"
              [appearance]="
                $selectedWorkflow() === workflow.id ? 'primary' : 'secondary'
              "
              (click)="setWorkflow(workflow.id)"
            >
              {{ workflow.title }}
            </button>
          }
        </div>
      }
    </section>

    <section
      class="flex gap-4 border-b border-(--tui-background-neutral-1-hover) px-2 py-1.5 text-(--tui-text-secondary)"
    >
      <span class="x-font-body-5">
        <strong class="text-(--tui-text-primary)">{{
          visibleRows.length
        }}</strong>
        {{ 'workflows.events_dialog.rows' | translate }}
      </span>
      <span class="x-font-body-5" style="color: var(--tui-status-positive)">
        <strong>{{ stats.successCount }}</strong>
        {{ 'workflows.events_dialog.successful' | translate }}
      </span>
      <span class="x-font-body-5" style="color: var(--tui-status-negative)">
        <strong>{{ stats.errorCount }}</strong>
        {{ 'workflows.events_dialog.errors' | translate }}
      </span>
      <span class="x-font-body-5" style="color: var(--tui-status-warning)">
        <strong>{{ stats.fileCount }}</strong>
        {{ 'workflows.events_dialog.file_based' | translate }}
      </span>
    </section>

    <tui-scrollbar class="min-h-0 min-w-0 flex-1">
      <div class="overflow-x-auto">
        <table class="workflow-log-table w-full border-collapse text-left">
          <colgroup>
            <col style="width: 90px" />
            <col style="width: 170px" />
            <col style="width: 190px" />
            <col style="width: 190px" />
            <col style="width: 95px" />
            <col />
            <col style="width: 36px" />
          </colgroup>
          <thead>
            <tr class="border-b border-(--tui-background-neutral-1-hover)">
              <th
                class="px-2 py-2 x-font-body-5-bold text-(--tui-text-secondary)"
              >
                {{ 'workflows.events_dialog.table_time' | translate }}
              </th>
              <th
                class="px-2 py-2 x-font-body-5-bold text-(--tui-text-secondary)"
              >
                {{ 'workflows.events_dialog.table_user' | translate }}
              </th>
              <th
                class="px-2 py-2 x-font-body-5-bold text-(--tui-text-secondary)"
              >
                {{ 'workflows.events_dialog.table_action' | translate }}
              </th>
              <th
                class="px-2 py-2 x-font-body-5-bold text-(--tui-text-secondary)"
              >
                {{ 'workflows.events_dialog.table_object' | translate }}
              </th>
              <th
                class="px-2 py-2 x-font-body-5-bold text-(--tui-text-secondary)"
              >
                {{ 'workflows.events_dialog.table_status' | translate }}
              </th>
              <th
                class="px-2 py-2 x-font-body-5-bold text-(--tui-text-secondary)"
              >
                {{ 'workflows.events_dialog.table_metadata' | translate }}
              </th>
              <th class="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            @for (row of visibleRows; track row.entry.id) {
              @let entry = row.entry;
              @let info = row.info;
              <tr
                class="cursor-pointer border-b border-(--tui-background-neutral-1-hover) hover:bg-(--tui-background-base-alt)"
                [class.selected]="$expandedId() === entry.id"
                (click)="toggleEntry(entry.id)"
              >
                <td class="px-2 py-2 align-top x-font-body-5">
                  <span
                    [tuiHint]="timePopover"
                    tuiHintAppearance="dark"
                    class="block"
                  >
                    <span class="block font-mono">{{
                      entry.createdAt | moscowTime
                    }}</span>
                    <span class="block font-mono text-(--tui-text-secondary)">{{
                      entry.createdAt | moscowDay
                    }}</span>
                  </span>
                  <ng-template #timePopover>
                    <div class="space-y-1 p-2 text-xs">
                      <div><strong>UTC:</strong> {{ entry.createdAt }}</div>
                      <div><strong>ID:</strong> {{ entry.id }}</div>
                    </div>
                  </ng-template>
                </td>

                <td class="px-2 py-2 align-top x-font-body-5">
                  <span
                    [tuiHint]="userPopover"
                    tuiHintAppearance="dark"
                    class="flex items-center gap-1.5"
                  >
                    <span
                      class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-(--tui-border-normal) x-font-body-5-bold text-(--tui-text-primary)"
                    >
                      {{ entry.actor.initials }}
                    </span>
                    <span class="min-w-0">
                      <span class="block x-font-body-5-bold">{{
                        entry.actor.name
                      }}</span>
                      <span
                        class="block truncate text-(--tui-text-secondary)"
                        >{{ entry.actor.email || '—' }}</span
                      >
                    </span>
                  </span>
                  <ng-template #userPopover>
                    <div class="space-y-1 p-2 text-xs">
                      <div><strong>ID:</strong> {{ entry.actor.id }}</div>
                      <div>
                        <strong
                          >{{
                            'workflows.events_dialog.full_name' | translate
                          }}:</strong
                        >
                        {{ entry.actor.fullName || entry.actor.name }}
                      </div>
                      <div>
                        <strong>Email:</strong> {{ entry.actor.email || '—' }}
                      </div>
                    </div>
                  </ng-template>
                </td>

                <td class="px-2 py-2 align-top x-font-body-5">
                  <span class="flex items-start gap-1.5">
                    <span
                      class="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
                      [style.background-color]="row.toneStyle.dotColor"
                    ></span>
                    <span>
                      <span class="block x-font-body-5-bold">{{
                        info.label
                      }}</span>
                      <span class="block text-(--tui-text-secondary)">{{
                        row.actionSubtitle
                      }}</span>
                    </span>
                  </span>
                </td>

                <td class="px-2 py-2 align-top x-font-body-5">
                  <span class="block x-font-body-5-bold">{{
                    row.entityTitle
                  }}</span>
                  <span class="block font-mono text-(--tui-text-secondary)">{{
                    row.entitySubtitle
                  }}</span>
                </td>

                <td class="px-2 py-2 align-top x-font-body-5">
                  <span
                    class="inline-block rounded-full px-2 py-0.5 x-font-body-5-bold"
                    [style.background-color]="
                      row.toneStyle.badgeBackgroundColor
                    "
                    [style.color]="row.toneStyle.badgeTextColor"
                  >
                    {{ row.statusText }}
                  </span>
                </td>

                <td class="px-2 py-2 align-top x-font-body-5">
                  <div class="flex flex-wrap gap-1">
                    <span
                      [tuiHint]="workflowPopover"
                      tuiHintAppearance="dark"
                      class="workflow-log-meta-token"
                    >
                      {{ 'workflows.events_dialog.workflow_short' | translate }}
                      <strong>{{ entry.workflowId | slice: 0 : 8 }}</strong>
                    </span>
                    <ng-template #workflowPopover>
                      <div class="space-y-1 p-2 text-xs">
                        <div>
                          <strong>Название:</strong> {{ row.entityTitle }}
                        </div>
                        <div><strong>ID:</strong> {{ entry.workflowId }}</div>
                      </div>
                    </ng-template>

                    @if (entry.formId) {
                      <span
                        [tuiHint]="idsPopover"
                        tuiHintAppearance="dark"
                        class="workflow-log-meta-token"
                      >
                        {{ 'workflows.events_dialog.form' | translate }}
                        <strong>{{ entry.formId }}</strong>
                      </span>
                      <ng-template #idsPopover>
                        <div class="space-y-1 p-2 text-xs">
                          <div>
                            <strong>ID формы:</strong> {{ entry.formId }}
                          </div>
                          <div>
                            <strong>ID сабмита:</strong>
                            {{ entry.submissionId || 'n/a' }}
                          </div>
                          <div>
                            <strong>Raw-тип:</strong> {{ entry.eventType }}
                          </div>
                        </div>
                      </ng-template>
                    }

                    @let entryFields = entry.fields ?? [];
                    @if (entryFields?.length) {
                      <span
                        [tuiHint]="fieldsPopover"
                        tuiHintAppearance="dark"
                        class="workflow-log-meta-token workflow-log-meta-token--blue"
                      >
                        {{ 'workflows.events_dialog.fields' | translate }}
                        <strong>{{ entryFields.length }}</strong>
                      </span>
                      <ng-template #fieldsPopover>
                        <div class="space-y-1 p-2 text-xs">
                          @for (
                            field of entryFields.slice(0, 6);
                            track field.key + $index
                          ) {
                            <div>
                              <strong>{{ field.key }}</strong> {{ field.value }}
                            </div>
                          }
                          @if (entryFields.length > 6) {
                            <div>
                              {{
                                'workflows.events_dialog.more_fields'
                                  | translate
                              }}
                              {{ entryFields.length - 6 }}
                            </div>
                          }
                        </div>
                      </ng-template>
                    }
                    @let entryFiles = entry.files ?? [];
                    @if (entryFiles?.length) {
                      <span
                        [tuiHint]="filesPopover"
                        tuiHintAppearance="dark"
                        class="workflow-log-meta-token workflow-log-meta-token--warn"
                      >
                        {{ 'workflows.events_dialog.files_short' | translate }}
                        <strong>{{ entryFiles.length }}</strong>
                      </span>
                      <ng-template #filesPopover>
                        <div class="space-y-1 p-2 text-xs">
                          @for (
                            logFile of entryFiles.slice(0, 5);
                            track logFile.name + $index
                          ) {
                            <div>
                              <strong>{{ logFile.category || 'файл' }}</strong>
                              {{ logFile.name }}
                            </div>
                          }
                          @if (entryFiles.length > 5) {
                            <div>
                              {{
                                'workflows.events_dialog.more_files' | translate
                              }}
                              {{ entryFiles.length - 5 }}
                            </div>
                          }
                        </div>
                      </ng-template>
                    }

                    @if (entry.device) {
                      <span
                        [tuiHint]="devicePopover"
                        tuiHintAppearance="dark"
                        class="workflow-log-meta-token"
                      >
                        {{ 'workflows.events_dialog.device_short' | translate }}
                        <strong>{{
                          entry.device.isMobile
                            ? ('workflows.events_dialog.mobile' | translate)
                            : ('workflows.events_dialog.desktop' | translate)
                        }}</strong>
                      </span>
                      <ng-template #devicePopover>
                        <div class="space-y-1 p-2 text-xs">
                          <div>
                            <strong>Тип:</strong> {{ entry.device.kind || '—' }}
                          </div>
                          <div>
                            <strong>ОС:</strong> {{ entry.device.os || '—' }}
                          </div>
                          <div>
                            <strong>Браузер:</strong>
                            {{ entry.device.browser || '—' }}
                          </div>
                          <div>
                            <strong>Viewport:</strong>
                            {{ entry.device.viewport || '—' }}
                          </div>
                          <div>
                            <strong>UA:</strong>
                            {{ entry.device.userAgent || '—' }}
                          </div>
                        </div>
                      </ng-template>
                    }
                  </div>
                </td>

                <td class="px-2 py-2 align-top">
                  <tui-icon
                    class="text-(--tui-text-secondary)"
                    [icon]="
                      $expandedId() === entry.id
                        ? '@tui.chevron-up'
                        : '@tui.chevron-down'
                    "
                  />
                </td>
              </tr>

              @if ($expandedId() === entry.id) {
                <tr style="background: var(--tui-background-base-alt)">
                  <td colspan="7" class="p-3">
                    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <section>
                        <h4
                          class="mb-2 x-font-body-5-bold text-(--tui-text-secondary)"
                        >
                          {{ 'workflows.events_dialog.form_data' | translate }}
                        </h4>
                        @if (entryFields.length) {
                          @for (
                            field of entryFields;
                            track field.key + $index
                          ) {
                            <div class="mb-1 flex gap-2 x-font-body-5">
                              <code class="text-(--tui-text-secondary)">{{
                                field.key
                              }}</code>
                              <span
                                ><strong>{{ field.label }}:</strong>
                                {{ field.value }}</span
                              >
                            </div>
                          }
                        } @else {
                          <span
                            class="x-font-body-5 text-(--tui-text-secondary)"
                            >{{
                              'workflows.events_dialog.no_files' | translate
                            }}</span
                          >
                        }
                      </section>

                      <section>
                        <h4
                          class="mb-2 x-font-body-5-bold text-(--tui-text-secondary)"
                        >
                          {{
                            'workflows.events_dialog.files_section' | translate
                          }}
                        </h4>
                        @if (entryFiles?.length) {
                          @for (
                            logFile of entryFiles;
                            track logFile.name + $index
                          ) {
                            <div
                              class="mb-1 flex items-start gap-2 x-font-body-5"
                            >
                              <code
                                class="flex-shrink-0 text-(--tui-text-secondary)"
                                >{{ logFile.fieldKey || 'file' }}</code
                              >
                              <span>
                                <strong>{{
                                  logFile.category ||
                                    ('workflows.events_dialog.file_default'
                                      | translate)
                                }}</strong>
                                <span
                                  class="block text-(--tui-text-secondary)"
                                  >{{ logFile.name }}</span
                                >
                              </span>
                            </div>
                          }
                        } @else {
                          <span
                            class="x-font-body-5 text-(--tui-text-secondary)"
                          >
                            {{ 'workflows.events_dialog.no_files' | translate }}
                          </span>
                        }
                      </section>

                      <section>
                        <h4
                          class="mb-2 x-font-body-5-bold text-(--tui-text-secondary)"
                        >
                          {{ 'workflows.events_dialog.raw_log' | translate }}
                        </h4>
                        <dl class="space-y-1 x-font-body-5">
                          <div>
                            <dt class="inline text-(--tui-text-secondary)">
                              {{
                                'workflows.events_dialog.event_label'
                                  | translate
                              }}:
                            </dt>
                            <dd class="inline">{{ entry.eventType }}</dd>
                          </div>
                          <div>
                            <dt class="inline text-(--tui-text-secondary)">
                              {{
                                'workflows.events_dialog.event_ids_label'
                                  | translate
                              }}:
                            </dt>
                            <dd class="inline">
                              {{ (entry.ids ?? [entry.id]).join(', ') }}
                            </dd>
                          </div>
                          <div>
                            <dt class="inline text-(--tui-text-secondary)">
                              {{
                                'workflows.events_dialog.regulations_label'
                                  | translate
                              }}:
                            </dt>
                            <dd class="inline font-mono">
                              {{ entry.workflowId }}
                            </dd>
                          </div>
                          <div>
                            <dt class="inline text-(--tui-text-secondary)">
                              {{
                                'workflows.events_dialog.valid_label'
                                  | translate
                              }}:
                            </dt>
                            <dd class="inline">
                              {{ entry.isSubmissionValid ?? 'n/a' }}
                            </dd>
                          </div>
                          <div>
                            <dt class="inline text-(--tui-text-secondary)">
                              {{
                                'workflows.events_dialog.time_utc_label'
                                  | translate
                              }}:
                            </dt>
                            <dd class="inline font-mono">
                              {{ entry.createdAt }}
                            </dd>
                          </div>
                          @if (entry.device) {
                            <div>
                              <dt class="inline text-(--tui-text-secondary)">
                                {{
                                  'workflows.events_dialog.device_label'
                                    | translate
                                }}:
                              </dt>
                              <dd class="inline">
                                {{ entry.device.kind }}, {{ entry.device.mode }}
                              </dd>
                            </div>
                          }
                        </dl>
                      </section>
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      @if (!visibleRows.length) {
        <div
          class="flex flex-col items-center justify-center gap-2 py-8 text-center"
        >
          <strong class="x-font-body-3-bold text-(--tui-text-primary)">{{
            'workflows.events_dialog.no_events' | translate
          }}</strong>
          <span class="x-font-body-4 text-(--tui-text-secondary)">{{
            'workflows.events_dialog.change_search_filters' | translate
          }}</span>
        </div>
      }
    </tui-scrollbar>
  }

  <footer
    class="flex justify-end border-t border-(--tui-background-neutral-1-hover) pt-3"
  >
    <button
      tuiButton
      type="button"
      appearance="secondary"
      size="m"
      (click)="close()"
    >
      {{ 'workflows.events_dialog.close' | translate }}
    </button>
  </footer>
</div>
